import { Address } from './address.domain';

export interface PartyGroupResponseList {
  partyGroupId: string;
  parties: unknown;
}

export interface LastUpdate {
  asOfDttm: Date;
}

export interface ContactStreetAddress {
  lastUpdate?: LastUpdate;
  usageCd: string;
  relationshipId?: string;
  address: Address;
}

export type ContactStreetAddressUsageType = 'HOME_ADDRESS' | 'MAILING_ADDRESS';

export interface Telephone {
  countryCodeNum: string;
  areaCodeNum: string;
  localNum: string;
}

export interface ContactTelephoneAddress extends ContactAddress {
  usageCd: string;
  telephone: Telephone;
}

export interface ContactEmailAddress extends ContactAddress {
  email: Email;
}

export interface ContactAddress {
  lastUpdate?: LastUpdate;
}

export interface Email {
  emailTxt: string;
}

export interface Employment {
  employerName: string;
  occupationTypeCd: string;
  industryTypeCd?: string;
  streetAddress?: Address;
  statusCd: EmploymentStatusType;
  lastUpdate?: LastUpdate;
}

export class Kyc {
  control: boolean;
  insider: boolean;
  pro: boolean;
}

export interface UserProfile {
  partyGroupResponseList: Array<PartyGroupResponseList>;
  contactEmailAddress: ContactEmailAddress;
  contactStreetAddresses: Array<ContactStreetAddress>;
  contactTelephoneAddresses: Array<ContactTelephoneAddress>;
  employment: Employment;
  kyc: Kyc;
  givenName: string;
  familyName: string;
  middleName: string;
  haltTradeInd: boolean;
  preferredFirstName?: string;
  preferredLastName?: string;
  pronoun?: PronounCode;
  gender?: Gender;
}

export class EmploymentDetails {
  employment: Employment;
  kyc: Kyc;
  insiderDetails: Array<Company>;
  controlPositionDetails: Array<Company>;
}

export class Company {
  name: string;
  title: string;
}

export enum ProfileDetails {
  EMPLOYMENT = 'employment',
  PHONE = 'phone',
  ADDRESS = 'address',
  EMAIL = 'email',
  GIEM = 'giem'
}

export type ProfileDetailsType = `${ProfileDetails}`;

export enum EmploymentStatus {
  EMPLOYED = 'EMPLOYED',
  SELF_EMPLOYED = 'SELF_EMPLOYED',
  RETIRED = 'RETIRED',
  UNEMPLOYED = 'UNEMPLOYED',
  STUDENT = 'STUDENT',
  HOMEMAKER = 'HOMEMAKER',
  AT_HOME_PARENT = 'AT_HOME_PARENT'
}

export type EmploymentStatusType = `${EmploymentStatus}`;

export class UserProfileUpdate {
  partyGroupList: Array<PartyGroupResponseList>;
  updateType: UpdateType;
  drafts?: unknown;
}

export enum UpdateType {
  PHONE = 'PHONE',
  EMAIL = 'EMAIL',
  EMPLOYMENT = 'EMPLOYMENT',
  ADDRESS = 'ADDRESS',
  PREFERENCE = 'PREFERENCE',
  ATTESTATION = 'ATTESTATION'
}

export type updateType = UpdateType;

export enum PhoneUsageCd {
  HOME = 'HOME_PHONE',
  MOBILE = 'MOBILE'
}

export class PhoneUpdate {
  telephoneAddresses: Array<TelephoneAddress>;
}

export class TelephoneAddress {
  telephone: TelephoneNumber;
  usageCd: PhoneUsageCd;
}

export class TelephoneNumber {
  telephoneNum: string;
}

export class EmailUpdate {
  emailAddresses: Array<EmailAddress>;
}

export enum AddressUsageCd {
  PRIMARY_RESIDENCE = 'PRIMARY_RESIDENCE',
  CUSTOMER_MAILING = 'CUSTOMER_MAILING'
}

export class EmailAddress {
  email: Email;
}

export class AddressInfo {
  addressUpdate: AddressUpdate;
  isMailingSame: boolean;
}

export class AddressUpdate {
  streetAddresses: Array<StreetAddress>;
}

export class StreetAddress {
  address: Address;
  usageCd: AddressUsageCd;
}

export class EmploymentKyc {
  employment: EmploymentUpdate;
  control: boolean;
  insider: boolean;
  pro: boolean;
}

export class EmploymentUpdate {
  employment: EmploymentInfo;
  kyc: KycDetails;
}

export class EmploymentInfo {
  employerName: string;
  occupationTypeCd: string;
  statusCd: EmploymentStatusType;
  streetAddress?: Address;
}

export class KycDetails {
  company: Array<CompanyDetails>;
}

export enum CompanyCd {
  COMPANY_CONTROL = 'COMPANY_CONTROL',
  COMPANY_OFFICER = 'COMPANY_OFFICER'
}

export class CompanyDetails {
  companyCd: CompanyCd;
  companyName: string;
  roleTxt: string;
}

export class NameGenderPronounUpdate {
  preference: NameGenderPronounInfo;
}

export class NameGenderPronounInfo {
  preferredFirstName?: string;
  preferredLastName?: string;
  pronoun?: PronounCode;
  gender?: Gender;
}

// this is created to work around eslint rule @typescript-eslint/explicit-module-boundary-types
export interface UserProfileInfo {
  profileInfo: KycUpdates;
}

export type KycUpdates = Partial<AddressUpdate &
                                 EmploymentUpdate &
                                 PhoneUpdate &
                                 EmailUpdate &
                                 NameGenderPronounUpdate>;

export enum Gender {
  BLANK = 'BLANK',
  PFR_NOT_TO_SAY = 'PFR_NOT_TO_SAY',
  MAN = 'MAN',
  WOMAN = 'WOMAN',
  X_NON_BINARY = 'X_NON_BINARY'
}

export enum PronounCode {
  BLANK = 'BLANK',
  PFR_NOT_TO_SAY = 'PFR_NOT_TO_SAY',
  HE_HIM_HIS = 'HE_HIM_HIS',
  SHE_HER_HERS = 'SHE_HER_HERS',
  THEY_THEM_THEIRS = 'THEY_THEM_THEIRS',
  HE_THEY = 'HE_THEY',
  SHE_THEY = 'SHE_THEY',
  THEY_HE = 'THEY_HE',
  THEY_SHE = 'THEY_SHE',
  HE_SHE_THEY = 'HE_SHE_THEY',
  SHE_HE_THEY = 'SHE_HE_THEY',
  THEY_HE_SHE = 'THEY_HE_SHE',
  THEY_SHE_HE = 'THEY_SHE_HE'
}
